<template>
  <section class="p-6 bg-grey-80">
    <h3 class="title-3">
      {{ $t.reservationTitle }}
    </h3>
    <p class="text-xs c-grey-20 my-2">
      {{ $t.requiredField }}
    </p>
    <base-form :class="brandClasses.form" :form :scroll-to-error="false" @submit="onSubmit">
      <div :class="brandClasses.fieldsWrapper">
        <vf-form-field v-slot="{ attrs }" name="firstName" :rule="validateRequired()">
          <vf-input v-model="form.firstName" v-bind="attrs" required>
            {{ separateNameFields ? $t.firstName : $t.name }}
          </vf-input>
        </vf-form-field>
        <vf-form-field v-slot="{ attrs }" v-if="separateNameFields" name="lastName" :rule="validateRequired()">
          <vf-input v-model="form.lastName" v-bind="attrs" required>
            {{ $t.lastName }}
          </vf-input>
        </vf-form-field>
        <vf-form-field v-slot="{ attrs }" name="email" :rule="[validateRequired(), validateEmail()]">
          <vf-input v-model="form.email" v-bind="attrs" required type="email">
            {{ $t.email }}
          </vf-input>
        </vf-form-field>
        <vf-form-field v-slot="{ attrs }" name="phone" :rule="[validateRequired(), validatePhone(form.phoneCode)]">
          <input-phone v-model="form.phone" v-model:code="form.phoneCode" v-bind="attrs" required use-country-code>
            {{ $t.mobileNumber }}
          </input-phone>
        </vf-form-field>
      </div>
      <div :class="brandClasses.ctaWrapper">
        <vf-form-field v-slot="{ attrs }" name="robisTerms" :rule="validateRequired()">
          <vf-checkbox v-model="form.robisTerms" v-bind="attrs" class="text-md">
            <form-legal-disclaimer :content="$t.reservationPolicy" :link-classes="{ privacy: 'font-normal' }" />
          </vf-checkbox>
        </vf-form-field>
        <vf-button class="mt-6 w-max" :loading type="submit" variant="primary">
          {{ $t.submit }}
        </vf-button>
      </div>
    </base-form>
  </section>
</template>

<script lang="ts" setup>
import type { StoreShippingAddress } from '#types/cart'

const props = defineProps<{
  cartId: string
  storeShippingAddress: StoreShippingAddress
}>()
const emit = defineEmits<{
  success: []
}>()
const countryCode = useCountryCode()
const { cart } = useApi()
const { $t } = useNuxtApp()
const toast = useToaster()
const { brandClasses, separateNameFields } = useAppConfig().components.form.robis

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(countryCode) || '',
  robisTerms: false
})

const loading = ref(false)
const { storeShippingAddress } = props
const { city, addressLine1, postalCode, country, } = storeShippingAddress

const onSubmit = async () => {
  loading.value = true
  try {
    const address = {
      ...form,
      firstName: (separateNameFields ? form.firstName : form.firstName.split(' ')[0]) || '',
      lastName: (separateNameFields ? form.lastName : form.firstName.split(' ').slice(1).join(' ')) || '',
      phone: form.phone
        ? formatE164phone(
            form.phone,
            form.phoneCode
          )
        : '',
      city,
      addressLine1,
      postalCode,
      country
    }
    await cart.$placeOrder({
      recaptcha_response: '',
      cartId: props.cartId,
      billingAddress: {
        ...storeShippingAddress,
        firstName: address.firstName,
        lastName: address.lastName,
        phone: address.phone,
        email: address.email,
        countryCode: address.country,
        subscriptions: {
          newsletterConsent: false
        }
      },
      paymentMethod: {
        code: 'PAY_IN_STORE',
        additionalData: []
      }
    }, { context: 'robis' })

    emit('success')
    closeDialogs()
    toast.add({
      autoClose: false,
      props: {
        type: 'success',
        title: $t.reservationSuccess.toastTitle,
        message: $t.reservationSuccess.toastMessage
      }
    })
  }
  catch {
    closeDialogs()
    toast.add({
      autoClose: false,
      props: {
        type: 'error',
        message: $t.reservationError
      }
    })
  }
  finally {
    loading.value = false
  }
}
</script>
